import React from 'react'
import { Box, Stack, Typography, styled } from '@mui/material'
import { IGridEdgeLine } from 'src/generated-types/grid-expansion'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import EdgeLineChoice from './EdgeLineChoice'
import { SectionLabel } from './ExpandGrid.style'
import { allReplaced, withReplaced } from './expandGrid.action'
import MoveReplaceToggle from './MoveReplaceToggle'

interface ExpandEdgeLinesProps {
  lines: IGridEdgeLine[]
  replaced: number[]
  onReplacedChange: (ids: number[]) => void
  // The line under the mouse, which the canvas picks out.
  onHover: (id?: number) => void
  t: (key: string, options?: object) => string
}

// The mooring lines on the side being expanded, and whether each moves or is replaced. Hovering a row finds its line
// on the canvas.
export const ExpandEdgeLines: React.FC<ExpandEdgeLinesProps> = ({ lines, replaced, onReplacedChange, onHover, t }) => {
  const lineIds = lines.map(line => line.id)

  return (
    <Stack spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
        <SectionLabel flex={1} minWidth={0}>
          {t('revision-expand-lines-title', { count: lines.length })}
        </SectionLabel>
        <MoveReplaceToggle
          sx={{ flex: 'none' }}
          replaced={allReplaced(lineIds, replaced)}
          onChange={replace => onReplacedChange(replace ? lineIds : [])}
          moveLabel={t('revision-expand-move-all')}
          replaceLabel={t('revision-expand-replace-all')}
        />
      </Stack>
      <Typography fontSize={12} color="text.secondary">
        {t('revision-expand-lines-hint')}
      </Typography>
      <Box>
        {lines.map(line => (
          <LineRow key={line.id} onMouseEnter={() => onHover(line.id)} onMouseLeave={() => onHover(undefined)}>
            <EdgeLineChoice
              name={line.name}
              fromCorner={line.fromCorner}
              toCorner={line.toCorner}
              replaced={replaced.includes(line.id)}
              onChange={replace => onReplacedChange(withReplaced(replaced, line.id, replace))}
              t={t}
            />
          </LineRow>
        ))}
      </Box>
    </Stack>
  )
}

export default ExpandEdgeLines

const LineRow = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 0.75),
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  '&:hover': { backgroundColor: getThemeColor(theme, MUIThemeColors.paleBlue) },
}))
